import {
  enumOf,
  useTableParams,
  type FieldSpec,
} from "@/hooks/useTableParams";
import { EmployeePayrollStatusEnum } from "@/utils/enums/payroll.enum";

/** `all` is these toolbars' no-filter sentinel (Radix Select forbids `""`). */
const withAll = (values: readonly string[]) => enumOf(["all", ...values]);

export const PAYSLIP_SORT_VALUES = [
  "periodStart-desc",
  "periodStart-asc",
  "netSalary-desc",
  "netSalary-asc",
  "payDate-desc",
  "payDate-asc",
];

const PAYSLIP_DEFAULTS = {
  status: "all",
  year: "all",
  sortKey: PAYSLIP_SORT_VALUES[0],
};

/**
 * The payslips and disputes tables are two tabs of the SAME url (switched by
 * `?tab=`), so each is namespaced — otherwise one tab's status filter and page
 * would leak into the other.
 */
export const useMsPayslipParams = () =>
  useTableParams<typeof PAYSLIP_DEFAULTS>({
    defaults: PAYSLIP_DEFAULTS,
    schema: {
      status: withAll(Object.values(EmployeePayrollStatusEnum)),
      // Years are generated at runtime, so they can't be enumerated here;
      // `toYearParam` drops "all" and anything non-numeric at the call site.
      year: "string",
      sortKey: enumOf(PAYSLIP_SORT_VALUES),
    } satisfies Partial<Record<keyof typeof PAYSLIP_DEFAULTS, FieldSpec>>,
    ns: "payslips",
  });

/** `"all"` or a junk value → undefined; a real year → a number. */
export const toYearParam = (value: string): number | undefined => {
  if (!value || value === "all") return undefined;
  const n = Number(value);
  return Number.isInteger(n) ? n : undefined;
};

const DISPUTE_DEFAULTS = { status: "all" };
const DISPUTE_SCHEMA = {
  status: enumOf(["all", "PENDING", "RESOLVED", "REJECTED"]),
} satisfies Partial<Record<keyof typeof DISPUTE_DEFAULTS, FieldSpec>>;

/** See the note on `useMsPayslipParams` for why this is namespaced. */
export const useMsDisputeParams = () =>
  useTableParams<typeof DISPUTE_DEFAULTS>({
    defaults: DISPUTE_DEFAULTS,
    schema: DISPUTE_SCHEMA,
    ns: "disputes",
  });

/**
 * The same table on its own route (`/my-space/payroll/disputes`), where it is
 * the only one on the page — so plain `?status=`, no namespace.
 */
export const useMsDisputePageParams = () =>
  useTableParams<typeof DISPUTE_DEFAULTS>({
    defaults: DISPUTE_DEFAULTS,
    schema: DISPUTE_SCHEMA,
  });
